import { useState, useEffect } from 'react';
import api from '../services/api';

export default function BookingAttachments({ bookingId, currentUserId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const MAX_FILES = 10;

  const getMimeIcon = (mimeType) => {
    if (!mimeType) return '📄';
    if (mimeType.includes('pdf')) return '📕';
    if (mimeType.includes('image')) return '🖼️';
    if (mimeType.includes('audio')) return '🎵';
    if (mimeType.includes('word') || mimeType.includes('docx')) return '📝';
    return '📄';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const truncateName = (name, maxLength = 30) => {
    if (name.length > maxLength) {
      return name.slice(0, maxLength - 3) + '...';
    }
    return name;
  };

  // Fetch files
  useEffect(() => {
    async function fetchFiles() {
      try {
        setLoading(true);
        const res = await api.get(`/bookings/${bookingId}/attachments`);
        setFiles(res.data.attachments || []);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchFiles();
  }, [bookingId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (files.length >= MAX_FILES) {
      alert(`Limite de ${MAX_FILES} fichiers atteinte`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('attachment', file);

      const res = await api.upload(`/bookings/${bookingId}/attachments`, formData);
      setFiles([...files, res.data.attachment]);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset input
    }
  };

  const handleDelete = async (attachmentId) => {
    if (!confirm('Supprimer ce fichier ?')) return;

    try {
      await api.delete(`/bookings/${bookingId}/attachments/${attachmentId}`);
      setFiles(files.filter((f) => f.id !== attachmentId));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownload = async (file) => {
    try {
      const blob = await api.download(`/bookings/${bookingId}/attachments/${file.id}/download`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const canDelete = (file) => file.uploadedBy === currentUserId;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 th-bg-surface">
        <div className="text-sm th-text-muted font-mono">Chargement...</div>
      </div>
    );
  }

  const quotaPercent = Math.round((files.length / MAX_FILES) * 100);

  return (
    <div className="space-y-4 th-bg-surface p-4 border th-border">
      {/* Upload button + quota */}
      <div className="flex items-center justify-between">
        <label className="relative">
          <button
            type="button"
            disabled={uploading || files.length >= MAX_FILES}
            className="th-btn-accent disabled:opacity-50"
          >
            {uploading ? '...' : 'Ajouter un fichier'}
          </button>
          <input
            type="file"
            onChange={handleUpload}
            accept=".pdf,.jpg,.png,.webp,.doc,.docx,.mp3,.wav"
            disabled={uploading || files.length >= MAX_FILES}
            className="hidden"
          />
        </label>
        <span className="text-xs font-mono th-text-muted">
          {files.length}/{MAX_FILES}
        </span>
      </div>

      {error && (
        <div className="text-xs text-status-rejected font-mono bg-status-rejected/10 p-2 border border-status-rejected/30">
          {error}
        </div>
      )}

      {/* Files list */}
      {files.length === 0 ? (
        <div className="text-center text-sm th-text-muted font-mono py-4">
          Aucun fichier
        </div>
      ) : (
        <div className="space-y-1 border th-border divide-y th-border">
          {files.map((file) => (
            <div key={file.id} className="th-bg p-3 flex items-center justify-between">
              <button
                type="button"
                onClick={() => handleDownload(file)}
                className="flex items-center gap-2 flex-1 text-left hover:text-accent transition-colors duration-150"
              >
                <span className="text-lg">{getMimeIcon(file.mimeType)}</span>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-sm font-mono th-text truncate hover:underline"
                    title={file.fileName}
                  >
                    {truncateName(file.fileName)}
                  </p>
                  <p className="text-xs th-text-muted font-mono">
                    {formatFileSize(file.fileSize)} · {formatDate(file.createdAt)}
                  </p>
                </div>
              </button>

              {canDelete(file) && (
                <button
                  onClick={() => handleDelete(file.id)}
                  className="ml-2 border border-status-rejected/30 text-status-rejected text-xs font-mono uppercase tracking-widest px-2 py-1 hover:bg-status-rejected/10 transition-colors duration-150 active:opacity-70 flex-shrink-0"
                  title="Supprimer ce fichier"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Progress bar */}
      <div className="w-full h-1 th-bg-elevated border-t th-border">
        <div
          className="h-full bg-accent transition-all duration-300"
          style={{ width: `${quotaPercent}%` }}
        />
      </div>
    </div>
  );
}
